import { ApiError } from '../api/client'
import { useCloseTimeAccount, useReopenTimeAccount, useTimeAccount } from './useZeiten'
import { monthLabel } from './timeMath'

/** Minuten-Saldo als ±H:MM (Integer-Minuten, nie Float — CLAUDE.md-Geldregel analog für Zeit). */
function fmtMin(min: number): string {
  const sign = min < 0 ? '−' : '+'
  const abs = Math.abs(min)
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')} h`
}

/**
 * Zeitkonto-Karte im Chef-Nachweis (Phase-1/Teilschritt 2): abgeschlossene Monats-Perioden (eingefroren,
 * GoBD) mit fortlaufendem Saldo, „Monat abschließen" für den nächsten abschließbaren Monat und „Wieder
 * öffnen" solange das 24-h-Korrekturfenster der jüngsten Periode läuft. Ein offener Monat wird weiterhin
 * live gerechnet (Auswertung/Nachweis) — die Karte zeigt die FESTGESCHRIEBENE Wahrheit für die Lohnwelt.
 */
export function TimeAccountCard({ userId, canManage }: { userId: number; canManage: boolean }) {
  const account = useTimeAccount(userId)
  const close = useCloseTimeAccount()
  const reopen = useReopenTimeAccount()

  if (account.isLoading) return <div className="muted">Zeitkonto wird geladen…</div>
  if (account.isError || !account.data) {
    return (
      <div className="error" role="alert">
        Zeitkonto konnte nicht geladen werden.
      </div>
    )
  }
  const { periods, nextClosable, reopenablePeriod } = account.data
  const err = close.error ?? reopen.error
  const errorMsg = err instanceof ApiError ? err.message : err ? 'Aktion fehlgeschlagen.' : null

  return (
    <div className="wf-card wf-zscard" aria-label="Zeitkonto">
      <div className="wf-zscard__label">Zeitkonto (abgeschlossene Monate)</div>
      {periods.length === 0 ? (
        <p className="hint">Noch kein Monat abgeschlossen — der Saldo läuft live. Der erste Abschluss startet das Konto (Startsaldo 0).</p>
      ) : (
        <table className="wf-table" aria-label="Abgeschlossene Perioden">
          <thead>
            <tr>
              <th>Monat</th>
              <th>Soll</th>
              <th>Ist</th>
              <th>Monat ±</th>
              <th>Kontostand</th>
              <th>abgeschlossen</th>
            </tr>
          </thead>
          <tbody>
            {periods.map((p) => (
              <tr key={p.period}>
                <td>{monthLabel(p.period)}</td>
                <td>{Math.floor(p.sollMin / 60)}:{String(p.sollMin % 60).padStart(2, '0')}</td>
                <td>{Math.floor(p.istMin / 60)}:{String(p.istMin % 60).padStart(2, '0')}</td>
                <td>{fmtMin(p.istMin - p.sollMin)}</td>
                <td>
                  <b>{fmtMin(p.closingBalanceMin)}</b>
                </td>
                <td className="muted">
                  {p.closedBy == null ? 'automatisch' : (p.closedByName ?? 'Chef')}
                  {canManage && reopenablePeriod === p.period ? (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="wf-btn wf-btn--sm wf-btn--ghost"
                        onClick={() => reopen.mutate({ userId, period: p.period })}
                        disabled={reopen.isPending}
                      >
                        Wieder öffnen
                      </button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {canManage && nextClosable ? (
        <div className="wf-listfoot">
          <button
            type="button"
            className="wf-btn wf-btn--primary"
            onClick={() => close.mutate({ userId, period: nextClosable })}
            disabled={close.isPending}
          >
            {close.isPending ? 'Schließe ab…' : `${monthLabel(nextClosable)} abschließen`}
          </button>
          <span className="hint">Friert Soll/Ist/Saldo fest (GoBD). 24 h lang rückgängig machbar, danach endgültig.</span>
        </div>
      ) : null}
      {errorMsg ? (
        <p className="error" role="alert">
          {errorMsg}
        </p>
      ) : null}
    </div>
  )
}
